// v-tooltip.js
const defaultOptions = {
  text: '',
  position: 'top',
  positionContainer: true,
  offset: 8,
  offsetX: null,
  offsetY: null,
  disabled: false,
  width: null,
  maxHeight: null,
  tooltipClass: '',
  textClass: '',
  align: null,
  auto: false,
  color: '#ffffff',
  bgColor: '#000000',
  hideDelay: 50,
  parentContainer: '', // Parent element ID for tooltip
  customStyle: {},
  html: false,
};

function parseOptions(value) {
  if (typeof value === 'string') return { ...defaultOptions, text: value };
  if (typeof value === 'object' && value !== null)
    return { ...defaultOptions, ...value };
  return defaultOptions;
}

function getViewportSpace(el, tooltipEl) {
  const rect = el.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();
  return {
    top: rect.top,
    bottom: window.innerHeight - rect.bottom,
    left: rect.left,
    right: window.innerWidth - rect.right,
    tooltipWidth: tooltipRect.width,
    tooltipHeight: tooltipRect.height,
  };
}

function choosePosition(el, tooltipEl, options) {
  if (!options.auto) return options.position;
  const space = getViewportSpace(el, tooltipEl);
  const tooltipWidth = space.tooltipWidth;
  const tooltipHeight = space.tooltipHeight;

  const fallbackOrder = {
    top: ['top', 'bottom', 'left', 'right'],
    bottom: ['bottom', 'top', 'left', 'right'],
    left: ['left', 'right', 'top', 'bottom'],
    right: ['right', 'left', 'top', 'bottom'],
  };

  const order = fallbackOrder[options.position] || [
    'top',
    'bottom',
    'left',
    'right',
  ];
  for (const pos of order) {
    switch (pos) {
      case 'top':
        if (space.top >= tooltipHeight) return 'top';
        break;
      case 'bottom':
        if (space.bottom >= tooltipHeight) return 'bottom';
        break;
      case 'left':
        if (space.left >= tooltipWidth) return 'left';
        break;
      case 'right':
        if (space.right >= tooltipWidth) return 'right';
        break;
    }
  }
  return options.position;
}

function adjustCrossAxis(el, tooltipEl, position, options) {
  const rect = el.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();
  let top = 0,
    left = 0;
  const offsetX = options.offsetX != null ? options.offsetX : options.offset;
  const offsetY = options.offsetY != null ? options.offsetY : options.offset;

  switch (position) {
    case 'top':
    case 'bottom':
      if (options.align === 'left') left = rect.left + offsetX;
      else if (options.align === 'right')
        left = rect.right - tooltipRect.width + offsetX;
      else left = rect.left + (rect.width - tooltipRect.width) / 2 + offsetX;

      if (left + tooltipRect.width > window.innerWidth)
        left = window.innerWidth - tooltipRect.width - 4;
      if (left < 0) left = 4;
      break;
    case 'left':
    case 'right':
      if (options.align === 'top') top = rect.top + offsetY;
      else if (options.align === 'bottom')
        top = rect.bottom - tooltipRect.height + offsetY;
      else top = rect.top + (rect.height - tooltipRect.height) / 2 + offsetY;

      if (top + tooltipRect.height > window.innerHeight)
        top = window.innerHeight - tooltipRect.height - 4;
      if (top < 0) top = 4;
      break;
  }
  return { top, left };
}

function positionTooltip(el, tooltipEl, options, lastMouseEvent) {
  if (!tooltipEl) return;
  let top = 0,
    left = 0;
  const offsetX = options.offsetX != null ? options.offsetX : options.offset;
  const offsetY = options.offsetY != null ? options.offsetY : options.offset;
  const finalPosition = choosePosition(el, tooltipEl, options);
  const rect = el.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();
  const container = tooltipEl.__tooltipContainer || document.body;
  const isBody = container === document.body;
  const containerRect = isBody
    ? { top: 0, left: 0 }
    : container.getBoundingClientRect();
  const containerScrollTop = isBody ? 0 : container.scrollTop;
  const containerScrollLeft = isBody ? 0 : container.scrollLeft;

  if (options.positionContainer) {
    switch (finalPosition) {
      case 'top':
        top = rect.top - tooltipRect.height - offsetY;
        left = rect.left + (rect.width - tooltipRect.width) / 2;
        break;
      case 'bottom':
        top = rect.bottom + offsetY;
        left = rect.left + (rect.width - tooltipRect.width) / 2;
        break;
      case 'left':
        left = rect.left - tooltipRect.width - offsetX;
        top = rect.top + (rect.height - tooltipRect.height) / 2;
        break;
      case 'right':
        left = rect.right + offsetX;
        top = rect.top + (rect.height - tooltipRect.height) / 2;
        break;
    }
  } else if (lastMouseEvent) {
    const clientX = lastMouseEvent.clientX;
    const clientY = lastMouseEvent.clientY;
    switch (finalPosition) {
      case 'top':
        top = clientY - tooltipRect.height - offsetY;
        left = clientX - tooltipRect.width / 2;
        break;
      case 'bottom':
        top = clientY + offsetY;
        left = clientX - tooltipRect.width / 2;
        break;
      case 'left':
        top = clientY - tooltipRect.height / 2;
        left = clientX - tooltipRect.width - offsetX;
        break;
      case 'right':
        top = clientY - tooltipRect.height / 2;
        left = clientX + offsetX;
        break;
    }
  }

  const adjusted = adjustCrossAxis(el, tooltipEl, finalPosition, options);
  if (['top', 'bottom'].includes(finalPosition)) {
    left = adjusted.left;
  } else if (['left', 'right'].includes(finalPosition)) {
    top = adjusted.top;
  }

  if (!isBody) {
    top = top - containerRect.top + containerScrollTop;
    left = left - containerRect.left + containerScrollLeft;
  }

  tooltipEl.style.top = `${top}px`;
  tooltipEl.style.left = `${left}px`;
}

function createTooltip(options) {
  const tooltipEl = document.createElement('div');
  tooltipEl.className = `custom-tooltip ${options.tooltipClass || ''}`;
  tooltipEl.style.pointerEvents = 'auto';

  const textEl = document.createElement('div');
  textEl.className = options.textClass || '';
  setTooltipContent(textEl, options);

  Object.assign(tooltipEl.style, {
    position: 'absolute',
    background: options.bgColor || '#333',
    color: options.color || '#fff',
    padding: '6px 10px',
    borderRadius: '4px',
    whiteSpace: 'normal',
    fontSize: '14px',
    zIndex: 9999,
    opacity: '1',
    transition: 'opacity 0.2s',
    width: options.width ? `${options.width}px` : 'auto',
    maxHeight: options.maxHeight ? `${options.maxHeight}px` : '150px',
    maxWidth: options.maxWidth ? `${options.maxWidth}px` : '300px',
    overflowY: 'auto',
    wordBreak: 'break-word',
    ...options.customStyle,
  });

  tooltipEl.appendChild(textEl);

  let container = document.body;
  if (options.parentContainer) {
    const el = document.getElementById(options.parentContainer);
    if (el && el instanceof HTMLElement) {
      container = el;
      if (getComputedStyle(container).position === 'static') {
        container.style.position = 'relative';
      }
    }
  }
  container.appendChild(tooltipEl);
  tooltipEl.__tooltipContainer = container;

  // Force reflow to get accurate dimensions
  tooltipEl.offsetWidth;

  return tooltipEl;
}

function setTooltipContent(textEl, options) {
  if (options.html) {
    textEl.innerHTML = options.text || '';
  } else {
    textEl.innerText = options.text;
  }
}

export default {
  bind(el, binding) {
    let tooltipEl = null;
    let hideTimeout = null;
    let lastMouseEvent = null;
    let isHoverEl = false;
    let isHoverTooltip = false;

    el.__tooltipOptions = parseOptions(binding.value);

    function updateHideTimeout() {
      if (hideTimeout) clearTimeout(hideTimeout);
      hideTimeout = setTimeout(() => {
        if (!isHoverEl && !isHoverTooltip) {
          if (tooltipEl) tooltipEl.style.opacity = '0';
          setTimeout(() => {
            if (tooltipEl && tooltipEl.parentNode)
              tooltipEl.parentNode.removeChild(tooltipEl);
            tooltipEl = null;
          }, 200);
        }
      }, el.__tooltipOptions.hideDelay);
    }

    function showTooltip(event) {
      const options = el.__tooltipOptions;
      if (!options || options.disabled) return;
      if (!options.positionContainer && event) lastMouseEvent = event;

      if (!tooltipEl) {
        tooltipEl = createTooltip(options);
        el.__tooltipEl = tooltipEl;

        tooltipEl.addEventListener('mouseenter', () => {
          isHoverTooltip = true;
          if (hideTimeout) clearTimeout(hideTimeout);
        });
        tooltipEl.addEventListener('mouseleave', () => {
          isHoverTooltip = false;
          updateHideTimeout();
        });
      }

      setTooltipContent(tooltipEl.querySelector('div'), options);
      positionTooltip(el, tooltipEl, options, lastMouseEvent);
      requestAnimationFrame(() => {
        if (tooltipEl) tooltipEl.style.opacity = '1';
      });
    }

    el.__onMouseEnter = (event) => {
      isHoverEl = true;
      showTooltip(event);
    };
    el.__onMouseLeave = () => {
      isHoverEl = false;
      updateHideTimeout();
    };
    el.__onMouseMove = (event) => {
      lastMouseEvent = event;
      if (tooltipEl && !el.__tooltipOptions.positionContainer) {
        positionTooltip(el, tooltipEl, el.__tooltipOptions, lastMouseEvent);
      }
    };

    el.addEventListener('mouseenter', el.__onMouseEnter);
    el.addEventListener('mouseleave', el.__onMouseLeave);
    el.addEventListener('mousemove', el.__onMouseMove);
  },

  update(el, binding) {
    const newOptions = parseOptions(binding.value);
    el.__tooltipOptions = newOptions;
    if (el.__tooltipEl) {
      setTooltipContent(el.__tooltipEl.querySelector('div'), newOptions);
      positionTooltip(el, el.__tooltipEl, newOptions, null);
    }
  },

  unbind(el) {
    el.removeEventListener('mouseenter', el.__onMouseEnter);
    el.removeEventListener('mouseleave', el.__onMouseLeave);
    el.removeEventListener('mousemove', el.__onMouseMove);

    if (el.__tooltipEl && el.__tooltipEl.parentNode) {
      el.__tooltipEl.parentNode.removeChild(el.__tooltipEl);
    }

    delete el.__tooltipOptions;
    delete el.__tooltipEl;
    delete el.__onMouseEnter;
    delete el.__onMouseLeave;
    delete el.__onMouseMove;
  },
};
